import {
  buildSentryReplayUrl,
  environment,
} from "../../config/environment";
import React, { useState } from 'react';
import { customFetch } from "../../utils/Auth";
import { useToast } from "../../hooks/use-toast";
import { CLIENT_LOG_EVENTS_INFO, CLIENT_LOG_EVENTS_ERROR } from "../../config/client-logs-events";

const ClientEventsTables = ({ logs, isLoading, error }) => {
  const { toast } = useToast();
  const [errorStatsExpanded, setErrorStatsExpanded] = useState(true);
  const [infoStatsExpanded, setInfoStatsExpanded] = useState(false);
  const [errorTableExpanded, setErrorTableExpanded] = useState(false);
  const [infoTableExpanded, setInfoTableExpanded] = useState(false);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const ts = typeof timestamp === "string" && /^\d+$/.test(timestamp)
      ? parseInt(timestamp, 10)
      : timestamp;
    if (typeof ts === "number" && ts < 1e12) {
      return new Date(ts * 1000).toLocaleString();
    }
    if (typeof ts === "number") {
      return new Date(ts).toLocaleString();
    }
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return String(timestamp);
    }
  };

  const parseLogItem = (item) => {
    // Handle DynamoDB format conversion (both raw DynamoDB format and DocumentClient format)
    const parseDynamoValue = (value) => {
      if (!value) return null;
      // Raw DynamoDB format (with type descriptors)
      if (value.S) return value.S;
      if (value.N) return value.N;
      if (value.M) {
        const result = {};
        for (const [key, val] of Object.entries(value.M)) {
          result[key] = parseDynamoValue(val);
        }
        return result;
      }
      // DocumentClient format (already converted) or plain value
      return value;
    };

    const result = {};
    if (item.user_name) result.user_name = parseDynamoValue(item.user_name);
    if (item.timestamp) result.timestamp = parseDynamoValue(item.timestamp);
    if (item.event) result.event = parseDynamoValue(item.event);
    if (item.eventData) result.eventData = parseDynamoValue(item.eventData);
    if (item.deployment) result.deployment = parseDynamoValue(item.deployment);
    if (item.patientId) result.patientId = parseDynamoValue(item.patientId);
    return result;
  };

  if (isLoading) {
    return (
      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-600">Loading logs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-600">No logs found.</p>
      </div>
    );
  }

  // Separate logs into ERROR and INFO categories
  const errorLogs = logs.filter(log => {
    const parsed = parseLogItem(log);
    return CLIENT_LOG_EVENTS_ERROR.includes(parsed.event);
  });
  
  const infoLogs = logs.filter(log => {
    const parsed = parseLogItem(log);
    return CLIENT_LOG_EVENTS_INFO.includes(parsed.event);
  });

  // Helper function to calculate stats for events
  const calculateStats = (tableLogs) => {
    const statsMap = {};
    
    tableLogs.forEach(log => {
      const parsed = parseLogItem(log);
      const eventName = parsed.event || 'Unknown';
      const doctorName = parsed.user_name || 'Unknown';
      
      if (!statsMap[eventName]) {
        statsMap[eventName] = {
          count: 0,
          doctors: new Set()
        };
      }
      
      statsMap[eventName].count++;
      if (doctorName !== 'Unknown') {
        statsMap[eventName].doctors.add(doctorName);
      }
    });
    
    // Convert to array and sort by count (descending)
    return Object.entries(statsMap)
      .map(([eventName, data]) => ({
        eventName,
        count: data.count,
        affectedDoctors: data.doctors.size,
        doctorList: Array.from(data.doctors).sort().join(', ')
      }))
      .sort((a, b) => b.count - a.count);
  };

  // Handler for timestamp click
  const handleTimestampClick = async (timestamp, userName) => {
    if (!timestamp || !userName) {
      console.log('Missing timestamp or userName');
      return;
    }

    try {
      // Convert timestamp to the format expected by the backend
      // The backend expects epoch seconds (as string or number)
      let timestampValue = timestamp;
      let logTimestampMs;
      
      console.log('Timestamp click - original value:', {
        timestamp,
        type: typeof timestamp,
        timestampString: String(timestamp)
      });
      
      if (typeof timestamp === "string" && /^\d+$/.test(timestamp)) {
        // Already in epoch seconds format
        timestampValue = timestamp;
        logTimestampMs = parseInt(timestamp, 10) * 1000;
        console.log('Parsed as epoch seconds:', { timestampValue, logTimestampMs, logTimestampUTC: new Date(logTimestampMs).toISOString() });
      } else if (typeof timestamp === "number") {
        // Convert to epoch seconds if it's milliseconds
        if (timestamp >= 1e12) {
          timestampValue = Math.floor(timestamp / 1000).toString();
          logTimestampMs = timestamp;
        } else {
          timestampValue = timestamp.toString();
          logTimestampMs = timestamp * 1000;
        }
        console.log('Parsed as number:', { timestampValue, logTimestampMs, logTimestampUTC: new Date(logTimestampMs).toISOString() });
      } else {
        // Try to parse and convert
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          timestampValue = Math.floor(date.getTime() / 1000).toString();
          logTimestampMs = date.getTime();
        } else {
          console.error('Invalid timestamp format:', timestamp);
          return;
        }
        console.log('Parsed as date string:', { timestampValue, logTimestampMs, logTimestampUTC: new Date(logTimestampMs).toISOString() });
      }

      const params = new URLSearchParams();
      params.append('userName', userName);
      params.append('timestamp', timestampValue);

      const response = await customFetch(
        `${environment.apiUrl}/doctors/sentry-replays?${params.toString()}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error fetching Sentry replays:', errorData);
        toast({
          title: "Error",
          description: "Failed to fetch Sentry replays",
          variant: "destructive",
        });
        return;
      }

      const data = await response.json();
      const replays = data.data || [];

      // Find the replay that contains the clicked timestamp
      // The timestamp should be between started_at and finished_at
      let matchingReplay = null;
      
      for (const replay of replays) {
        const startedAt = new Date(replay.started_at).getTime();
        const finishedAt = new Date(replay.finished_at).getTime();
        // Use inclusive range: timestamp can be exactly at start or end
        const isWithinRange = logTimestampMs >= startedAt && logTimestampMs <= finishedAt;
        
        const timeDiffBefore = logTimestampMs - startedAt;
        const timeDiffAfter = finishedAt - logTimestampMs;
        const calculatedOffset = isWithinRange ? Math.floor((logTimestampMs - startedAt) / 1000) : null;
        
        console.log('Checking replay:', {
          id: replay.id,
          started_at: replay.started_at,
          started_at_ms: startedAt,
          started_at_PST: new Date(startedAt).toLocaleString('en-US', {timeZone: 'America/Los_Angeles'}),
          finished_at: replay.finished_at,
          finished_at_ms: finishedAt,
          finished_at_PST: new Date(finishedAt).toLocaleString('en-US', {timeZone: 'America/Los_Angeles'}),
          logTimestampMs,
          logTimestampUTC: new Date(logTimestampMs).toISOString(),
          logTimestampPST: new Date(logTimestampMs).toLocaleString('en-US', {timeZone: 'America/Los_Angeles'}),
          isWithinRange,
          timeDiffBefore_ms: timeDiffBefore,
          timeDiffBefore_seconds: Math.floor(timeDiffBefore / 1000),
          timeDiffAfter_ms: timeDiffAfter,
          timeDiffAfter_seconds: Math.floor(timeDiffAfter / 1000),
          calculatedOffset
        });
        
        if (isWithinRange) {
          matchingReplay = replay;
          break;
        }
      }

      let timeOffsetSeconds = 0;

      if (matchingReplay) {
        // Exact match - calculate time offset from started_at
        const startedAtMs = new Date(matchingReplay.started_at).getTime();
        timeOffsetSeconds = Math.floor((logTimestampMs - startedAtMs) / 1000);
        console.log('Exact match found:', {
          replayId: matchingReplay.id,
          startedAtMs,
          logTimestampMs,
          timeOffsetSeconds,
          startedAtUTC: new Date(startedAtMs).toISOString(),
          logTimestampUTC: new Date(logTimestampMs).toISOString()
        });
      } else {
        // If no exact match, find the closest replay
        // Check if timestamp is within 5 minutes of any replay (before or after)
        const FIVE_MINUTES_MS = 5 * 60 * 1000;
        
        const closestReplay = replays
          .map(replay => {
            const startedAt = new Date(replay.started_at).getTime();
            const finishedAt = new Date(replay.finished_at).getTime();
            
            // Calculate time difference - prefer replays that contain or are close to the timestamp
            let timeDiff;
            if (logTimestampMs >= startedAt && logTimestampMs <= finishedAt) {
              // Timestamp is within replay (shouldn't happen here, but just in case)
              timeDiff = 0;
            } else if (logTimestampMs < startedAt) {
              // Timestamp is before replay started
              timeDiff = startedAt - logTimestampMs;
            } else {
              // Timestamp is after replay finished
              timeDiff = logTimestampMs - finishedAt;
            }
            
            return { replay, timeDiff, startedAt, finishedAt };
          })
          .filter(({ timeDiff }) => timeDiff <= FIVE_MINUTES_MS)
          .sort((a, b) => a.timeDiff - b.timeDiff)[0];

        if (closestReplay) {
          // Use the closest replay
          matchingReplay = closestReplay.replay;
          const { startedAt, finishedAt } = closestReplay;
          
          if (logTimestampMs < startedAt) {
            // Timestamp is before replay started
            // Since we're within 5 minutes, still open the replay but start at beginning
            // Note: We can't use negative t values, so we start at t=0
            const secondsBeforeStart = Math.floor((startedAt - logTimestampMs) / 1000);
            timeOffsetSeconds = 0;
            console.log('Timestamp before replay, using t=0:', {
              logTimestampUTC: new Date(logTimestampMs).toISOString(),
              logTimestampPST: new Date(logTimestampMs).toLocaleString('en-US', {timeZone: 'America/Los_Angeles'}),
              replayStartedAt: new Date(startedAt).toISOString(),
              replayStartedAtPST: new Date(startedAt).toLocaleString('en-US', {timeZone: 'America/Los_Angeles'}),
              secondsBeforeStart,
              note: 'Event is before replay starts, opening replay at beginning (t=0)'
            });
          } else if (logTimestampMs > finishedAt) {
            // Timestamp is after replay finished, go to end (or near end)
            // Calculate total duration and use most of it
            const duration = finishedAt - startedAt;
            timeOffsetSeconds = Math.floor(duration / 1000) - 10; // 10 seconds before end
            console.log('Timestamp after replay, using near end:', {
              logTimestampUTC: new Date(logTimestampMs).toISOString(),
              replayFinishedAt: new Date(finishedAt).toISOString(),
              durationSeconds: Math.floor(duration / 1000),
              timeOffsetSeconds
            });
          } else {
            // Timestamp is within replay (shouldn't reach here since we check exact match first, but handle it)
            timeOffsetSeconds = Math.floor((logTimestampMs - startedAt) / 1000);
            console.log('Timestamp within replay (fallback):', {
              logTimestampUTC: new Date(logTimestampMs).toISOString(),
              timeOffsetSeconds
            });
          }
        } else {
          toast({
            title: "Sentry Log not found",
            description: "Sentry Log not found for this event",
            variant: "destructive",
          });
          return;
        }
      }

      // Open Sentry replay URL in new tab
      const sentryUrl = buildSentryReplayUrl(
        matchingReplay.id,
        timeOffsetSeconds
      );
      console.log('Opening Sentry replay:', {
        replayId: matchingReplay.id,
        timeOffsetSeconds,
        url: sentryUrl,
        logTimestampUTC: new Date(logTimestampMs).toISOString(),
        replayStartedAt: matchingReplay.started_at,
        replayFinishedAt: matchingReplay.finished_at
      });
      window.open(sentryUrl, '_blank');
    } catch (err) {
      console.error('Error fetching Sentry replays:', err);
      toast({
        title: "Error",
        description: "Failed to fetch Sentry replays",
        variant: "destructive",
      });
    }
  };

  // Helper function to render a table
  const renderTable = (tableLogs, title, bgColor, headerBgColor, isStatsExpanded, setIsStatsExpanded, isTableExpanded, setIsTableExpanded) => {
    if (tableLogs.length === 0) return null;
    
    const stats = calculateStats(tableLogs);
    
    return (
      <div className={`mt-8 ${bgColor} border border-gray-200 rounded-lg overflow-hidden`}>
        <div className={`px-4 py-3 ${headerBgColor} border-b border-gray-200`}>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">
              {title} ({tableLogs.length} {tableLogs.length === 1 ? 'log' : 'logs'})
            </h3>
          </div>
        </div>
        
        {/* Statistics Section */}
        <div className={`px-4 py-2 ${bgColor} border-b border-gray-200`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700">Statistics</h4>
            <button
              type="button"
              onClick={() => setIsStatsExpanded(!isStatsExpanded)}
              className="text-xs text-ktx-purple hover:text-purple-700 underline"
            >
              {isStatsExpanded ? 'Collapse' : 'Expand'}
            </button>
          </div>
          {isStatsExpanded && (
            <div className="space-y-2">
              {stats.map((stat, index) => (
                <div key={index} className="text-xs">
                  <div className="font-medium text-gray-900">
                    <span className="font-semibold">{stat.eventName}</span>
                    <span className="text-gray-600"> - {stat.count} {stat.count === 1 ? 'occurrence' : 'occurrences'}</span>
                    <span className="text-gray-600"> - {stat.affectedDoctors} {stat.affectedDoctors === 1 ? 'doctor' : 'doctors'}</span>
                  </div>
                  {stat.doctorList && (
                    <div className="text-gray-500 ml-3 mt-0.5 text-xs">
                      {stat.doctorList}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Table Toggle Button */}
        <div className={`px-4 py-2 ${bgColor} border-b border-gray-200`}>
          <button
            type="button"
            onClick={() => setIsTableExpanded(!isTableExpanded)}
            className="text-xs text-ktx-purple hover:text-purple-700 underline"
          >
            {isTableExpanded ? 'Collapse Table' : 'Expand Table'}
          </button>
        </div>
        
        {isTableExpanded && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={headerBgColor}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Doctor Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform/Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Data
                  </th>
                </tr>
              </thead>
              <tbody className={`${bgColor} divide-y divide-gray-200`}>
                {tableLogs.map((log, index) => {
                  const parsed = parseLogItem(log);
                  return (
                    <tr key={index} className={bgColor === 'bg-red-50' ? 'hover:bg-red-100' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          type="button"
                          onClick={() => handleTimestampClick(parsed.timestamp, parsed.user_name)}
                          className="text-ktx-purple hover:text-purple-700 hover:underline cursor-pointer"
                        >
                          {formatTimestamp(parsed.timestamp)}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {parsed.user_name || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {parsed.event || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {parsed.deployment
                          ? `${parsed.deployment.version || ''} (${parsed.deployment.platform || ''})`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {parsed.patientId || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="max-w-md break-words">
                          {parsed.eventData && Object.keys(parsed.eventData).length > 0
                            ? JSON.stringify(parsed.eventData, null, 2)
                            : '-'}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {renderTable(errorLogs, 'ERROR Events', 'bg-red-50', 'bg-red-100', errorStatsExpanded, setErrorStatsExpanded, errorTableExpanded, setErrorTableExpanded)}
      {renderTable(infoLogs, 'INFO Events', 'bg-white', 'bg-gray-50', infoStatsExpanded, setInfoStatsExpanded, infoTableExpanded, setInfoTableExpanded)}
    </>
  );
};

export default ClientEventsTables;

